import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectConfigSelections, selectConfigFilling,
  selectConfigPerCell,
  selectConfigWeekPortions,
  selectConfigTargets,
  selectConfigPlan,
  selectConfigShown,
  toggleSlot,
  setCellOverride,
  removeCellOverride,
  setSelections,
} from '../../../redux/mealPlanUISlice';
import { selectRecipeIndex } from '../../../redux/recipeIndexSlice';
import {
  MEALS,
  DAY_SHORT_KEYS,
  dayShortKey,
  dayFullKey,
  dayOf,
  rowOf,
  slotKey,
  revealOrder,
} from '../../weekly/v2/constants';
import { useTranslation } from 'react-i18next';
import { resolveRecipeTitle } from '../../../utils/recipeTitle';
import { Paprika } from '../../../styles/theme';
import { ConfigCell } from './ConfigCell';
import { CellPopover } from './CellPopover';
import type { Rect } from './popoverPlacement';
import { SuggCard } from './SuggCard';
import { ShimmerBox } from '../../weekly/v2/primitives/ShimmerBox';
import { PortionsModal } from './PortionsModal';
import { CONFIG_ACTION_BAR_HEIGHT } from './ConfigActionBar';
import type { ConfigPhase, DietKind } from '../../../redux/mealPlanUISlice';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import { todayIso, addDays, parseIsoDate } from '../../../utils/localDate';

interface PopoverState {
  sk: number;
  /** Anchor cell in window coordinates. */
  anchor: Rect;
  /** Overlay host in window coordinates. */
  origin: Rect;
}

interface Props {
  phase: ConfigPhase;
  weekStart: string;
  onCellShuffle: (slotKey: number) => void;
  onCellDetail: (slotKey: number) => void;
}

// Static slot sets for the quick-select chips.
const DINNER_SLOTS = Array.from({ length: 7 }, (_, di) => slotKey(2, di));  // soupers
const LUNCH_SLOTS = Array.from({ length: 7 }, (_, di) => slotKey(1, di));   // dîners
const ALL_SLOTS = (() => {
  const slots: number[] = [];
  for (let mi = 0; mi < 3; mi++) {
    for (let di = 0; di < 7; di++) {
      if (MEALS[mi].weekendOnly && di < 5) continue;
      slots.push(slotKey(mi, di));
    }
  }
  return slots;
})();

function getRecipeDietaryRole(recipe: { diets?: string[] } | undefined, kind: DietKind): DietaryRole | undefined {
  if (kind === 'fish') return 'fish';
  if (kind === 'veg') return 'vegetarian';
  if (kind === 'meat') return 'meat';
  if (!recipe) return undefined;
  const diets = (recipe.diets ?? []).map((d: string) => d.toLowerCase());
  if (diets.some((d: string) => d.includes('vegan') || d.includes('vegetarian'))) return 'vegetarian';
  if (diets.some((d: string) => d.includes('pescatarian') || d.includes('fish') || d.includes('seafood'))) return 'fish';
  return undefined;
}

function getDayNumber(weekStart: string, di: number): number {
  return parseIsoDate(addDays(weekStart, di)).getDate();
}

function isToday(weekStart: string, di: number): boolean {
  return addDays(weekStart, di) === todayIso();
}

function QuickChip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.chip, active && styles.chipActive]}>
      {active && <Feather name="check" size={12} color={Paprika.primary} />}
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </Pressable>
  );
}

export function ConfigGrid({ phase, weekStart, onCellShuffle, onCellDetail }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const selections = useAppSelector(selectConfigSelections);
  // A generation started from the week view has no config selection: the
  // slots it fills are what the thinking shimmer and the cascade run on.
  const filling = useAppSelector(selectConfigFilling);
  const perCell = useAppSelector(selectConfigPerCell);
  const defaultPortions = useAppSelector(selectConfigWeekPortions);
  const targets = useAppSelector(selectConfigTargets);
  const configPlan = useAppSelector(selectConfigPlan);
  const configShown = useAppSelector(selectConfigShown);
  const recipeIndex = useAppSelector(selectRecipeIndex);

  const [popover, setPopoverState] = useState<PopoverState | null>(null);
  const [portionsModalFor, setPortionsModalFor] = useState<number | null>(null);

  // The overlay host the popover renders into, and one ref per cell so the
  // panel can be anchored to the cell it belongs to.
  const overlayRef = useRef<View | null>(null);
  const cellRefs = useRef<Record<number, View | null>>({});

  // Mirrors `popover` so press handlers decide against the latest value even
  // when the outside-press listener has just closed the panel in the same
  // pointerdown -> click sequence.
  const popoverRef = useRef<PopoverState | null>(null);
  const setPopover = useCallback((next: PopoverState | null) => {
    popoverRef.current = next;
    setPopoverState(next);
  }, []);
  const closePopover = useCallback(() => setPopover(null), [setPopover]);

  const isConfig = phase === 'config';
  const isThinking = phase === 'thinking';
  const isCascadeOrDone = phase === 'cascade' || phase === 'done';

  /**
   * Generation takes the grid over: a cell panel left open from the settings
   * phase would hover over the cascade — and would fight the Escape shortcut
   * that cancels the generation, since it closes itself on that same key.
   */
  useEffect(() => {
    if (isConfig) return;
    setPopover(null);
    setPortionsModalFor(null);
  }, [isConfig, setPopover]);

  /**
   * Measures the cell and the overlay host, then anchors the panel to the cell.
   * Both rects are in window coordinates; the panel converts to host-relative
   * coordinates itself and keeps the window rect for outside-press hit-testing.
   */
  const openPopover = useCallback((sk: number) => {
    const cell = cellRefs.current[sk];
    const host = overlayRef.current;
    if (!cell || !host) return;
    host.measureInWindow((ox, oy, ow, oh) => {
      cell.measureInWindow((cx, cy, cw, ch) => {
        setPopover({
          sk,
          anchor: { x: cx, y: cy, width: cw, height: ch },
          origin: { x: ox, y: oy, width: ow, height: oh },
        });
      });
    });
  }, [setPopover]);

  function handleCellPress(sk: number, selected: boolean) {
    if (isCascadeOrDone) return;
    if (!selected) {
      dispatch(toggleSlot(sk));
      return;
    }
    // A press on the cell that owns the open panel closes it; anything else
    // opens that cell's panel — one click either way.
    if (popoverRef.current?.sk === sk) {
      closePopover();
      return;
    }
    openPopover(sk);
  }

  // A chip is "active" when every slot in its set is currently selected.
  const dinnersActive = DINNER_SLOTS.every((s) => selections.includes(s));
  const lunchesActive = LUNCH_SLOTS.every((s) => selections.includes(s));
  const allActive = ALL_SLOTS.every((s) => selections.includes(s));

  // Toggle a slot set: if all present, remove them; otherwise add them.
  function toggleSet(set: number[], active: boolean) {
    if (active) {
      dispatch(setSelections(selections.filter((s) => !set.includes(s))));
    } else {
      dispatch(setSelections([...new Set([...selections, ...set])]));
    }
  }

  function handleClear() {
    dispatch(setSelections([]));
  }

  const gridStyle: any = Platform.OS === 'web'
    ? {
        display: 'grid',
        gridTemplateColumns: '52px repeat(7, minmax(0, 1fr))',
        columnGap: 9,
        rowGap: 11,
      }
    : {
        flexDirection: 'column',
      };

  return (
    <View style={styles.root}>
      {/* Quick-select bar */}
      {isConfig && (
        <View style={styles.quickBar}>
          <Text style={styles.quickLabel}>{t('mealConfig.quickSelect')}</Text>
          <QuickChip label={t('mealConfig.allDinners')} active={dinnersActive} onPress={() => toggleSet(DINNER_SLOTS, dinnersActive)} />
          <QuickChip label={t('mealConfig.allLunches')} active={lunchesActive} onPress={() => toggleSet(LUNCH_SLOTS, lunchesActive)} />
          <QuickChip label={t('mealConfig.wholeWeek')} active={allActive} onPress={() => toggleSet(ALL_SLOTS, allActive)} />
          {selections.length > 0 && (
            <Pressable onPress={handleClear} style={styles.clearBtn}>
              <Text style={styles.quickBtnClear}>{t('mealConfig.clear')}</Text>
            </Pressable>
          )}
        </View>
      )}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={() => { if (popoverRef.current) closePopover(); }}
      >
        {/* Day header row */}
        <View style={[gridStyle, styles.headerRow]}>
          {/* label column placeholder */}
          <View />
          {DAY_SHORT_KEYS.map((dayKey, di) => {
            const dateNum = getDayNumber(weekStart, di);
            const today = isToday(weekStart, di);
            return (
              <View key={dayKey} style={styles.dayHeader}>
                <Text style={styles.dayHeaderText}>{t(dayShortKey(di))}</Text>
                <View style={[styles.dateNumBadge, today && styles.dateNumBadgeToday]}>
                  <Text style={[styles.dateNumText, today && styles.dateNumTextToday]}>{dateNum}</Text>
                </View>
              </View>
            );
          })}
        </View>

        {/* Meal rows */}
        {MEALS.map((meal, mi) => (
          <View key={meal.id} style={[gridStyle, styles.mealRow]}>
            {/* Row label */}
            <View style={styles.rowLabel}>
              <Text style={styles.rowLabelText}>{t(meal.labelKey)}</Text>
            </View>

            {/* Cells */}
            {Array.from({ length: 7 }, (_, di) => {
              const sk = slotKey(mi, di);
              const isFaint = meal.weekendOnly && di < 5;
              if (isFaint) return <View key={di} style={styles.faintSlot} />;

              const selected = selections.includes(sk) || (!isConfig && filling.includes(sk));
              const override = perCell[sk];
              const portions = override?.portions ?? defaultPortions;
              const kind = override?.kind ?? 'auto';
              const recipeId = configPlan[sk];
              const recipe = recipeId ? recipeIndex.find((r) => r.id === recipeId) : undefined;
              const shown = configShown.includes(sk);

              if (isCascadeOrDone && selected) {
                return (
                  <View key={di} style={{ position: 'relative' }}>
                    {recipe ? (
                      <SuggCard
                        title={resolveRecipeTitle(recipe, i18n.language)}
                        image={recipe.image}
                        portions={portions}
                        minutes={recipe.readyInMinutes}
                        dietaryRole={getRecipeDietaryRole(recipe, kind)}
                        source={recipe.sourceUrl}
                        rating={recipe.sourceRating}
                        ratingCount={recipe.sourceRatingCount}
                        shown={shown}
                        onShuffle={() => onCellShuffle(sk)}
                        onPortionsTap={() => setPortionsModalFor(sk)}
                        onCardPress={() => onCellDetail(sk)}
                      />
                    ) : (
                      <View style={styles.shimmerCard}>
                        <ShimmerBox width="100%" height={268} borderRadius={10} />
                      </View>
                    )}
                  </View>
                );
              }

              if (isThinking && selected) {
                return (
                  <View key={di} style={styles.shimmerCard}>
                    <ShimmerBox width="100%" height={246} borderRadius={10} />
                  </View>
                );
              }

              return (
                <View key={di} ref={(r) => { cellRefs.current[sk] = r; }}>
                  <ConfigCell
                    slotKey={sk}
                    selected={selected}
                    portions={portions}
                    kind={kind}
                    onPress={() => handleCellPress(sk, selected)}
                    onLongPress={() => selected && openPopover(sk)}
                  />
                </View>
              );
            })}
          </View>
        ))}
      </ScrollView>

      {/*
        Overlay host. The popover used to live inside its grid cell, where
        react-native-web's per-View stacking context trapped it under the row
        below. Rendering it here — last child of the root, outside the
        ScrollView — is what lets it float over the whole grid.
      */}
      <View ref={overlayRef} style={styles.overlayHost} pointerEvents="box-none">
        {popover && (() => {
          const sk = popover.sk;
          const override = perCell[sk];
          const portions = override?.portions ?? defaultPortions;
          const kind = override?.kind ?? 'auto';
          return (
            <CellPopover
              title={`${t(dayFullKey(dayOf(sk)))} · ${t(MEALS[rowOf(sk)].labelKey)}`}
              anchor={popover.anchor}
              origin={popover.origin}
              bottomInset={CONFIG_ACTION_BAR_HEIGHT}
              portions={portions}
              kind={kind}
              defaultPortions={defaultPortions}
              onPortionsChange={(n) => {
                dispatch(setCellOverride({ slotKey: sk, override: { portions: n, kind } }));
              }}
              onKindChange={(k) => {
                dispatch(setCellOverride({ slotKey: sk, override: { portions, kind: k } }));
              }}
              onRemove={() => {
                dispatch(removeCellOverride(sk));
                dispatch(toggleSlot(sk));
                closePopover();
              }}
              onClose={closePopover}
            />
          );
        })()}
      </View>

      {/* Portions modal for SuggCard tap */}
      {portionsModalFor !== null && (
        <PortionsModal
          visible
          portions={perCell[portionsModalFor]?.portions ?? defaultPortions}
          onConfirm={(n) => {
            const kind = perCell[portionsModalFor]?.kind ?? 'auto';
            dispatch(setCellOverride({ slotKey: portionsModalFor, override: { portions: n, kind } }));
          }}
          onClose={() => setPortionsModalFor(null)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    paddingHorizontal: 24,
  },
  quickBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 0,
    marginBottom: 8,
  },
  quickLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 11,
    paddingVertical: 5,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: 'transparent',
    backgroundColor: Paprika.bgSunk,
  },
  chipActive: {
    backgroundColor: Paprika.brandTint,
    borderColor: Paprika.brandSoft,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  chipTextActive: {
    color: Paprika.primary,
    fontFamily: 'Inter-SemiBold',
  },
  clearBtn: {
    paddingHorizontal: 6,
    paddingVertical: 5,
  },
  quickBtnClear: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: CONFIG_ACTION_BAR_HEIGHT,
  },
  headerRow: {
    marginBottom: 4,
  },
  dayHeader: {
    alignItems: 'center',
    paddingVertical: 4,
    gap: 2,
  },
  dayHeaderText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.textMuted,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  dateNumBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dateNumBadgeToday: {
    backgroundColor: Paprika.primary,
  },
  dateNumText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  dateNumTextToday: {
    color: '#fff',
    fontFamily: 'Inter-SemiBold',
  },
  mealRow: {
    marginBottom: 11,
  },
  rowLabel: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 52,
  },
  rowLabelText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
    transform: [{ rotate: '-90deg' }],
    textAlign: 'center',
  },
  faintSlot: {
    height: 118,
    borderRadius: 8,
    backgroundColor: Paprika.bgSunk,
    opacity: 0.2,
  },
  shimmerCard: {
    borderRadius: 10,
    overflow: 'hidden',
  },
  overlayHost: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
